<template>
  <Dialog
    :is-open="isOpen"
    :title="isEditing ? 'Edit Topic' : 'New Topic'"
    :show-close-button="false"
    @close="close"
  >
    <div :class="$style.formGroup">
      <label for="topicName">Topic Name</label>
      <input
        id="topicName"
        v-model="topicName"
        type="text"
        placeholder="Enter topic name"
        @keyup.enter="save"
      />
    </div>
    <div :class="$style.dialogActions">
      <button @click="close">Cancel</button>
      <button @click="save" :disabled="!topicName.trim()">Save</button>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { topicService } from "../services/topicService";
import Dialog from "./Dialog.vue";

const props = defineProps<{
  isOpen: boolean;
  topicId?: string;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save", topicId: string): void;
}>();

const topicName = ref("");
const isEditing = computed(() => !!props.topicId);

watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue && props.topicId) {
      const topic = topicService.getTopicById(props.topicId);
      if (topic) {
        topicName.value = topic.name;
      }
    } else {
      topicName.value = "";
    }
  }
);

function close() {
  emit("close");
}

function save() {
  if (!topicName.value.trim()) return;

  let topicId: string;
  if (props.topicId) {
    const updatedTopic = topicService.updateTopic(
      props.topicId,
      topicName.value.trim()
    );
    if (updatedTopic) {
      topicId = updatedTopic.id;
      emit("save", topicId);
      close();
    }
  } else {
    const newTopic = topicService.createTopic(topicName.value.trim());
    topicId = newTopic.id;
    emit("save", topicId);
    close();
  }
}
</script>

<style module>
.formGroup {
  margin-bottom: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
}

input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 14px;
  box-sizing: border-box;
}

input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.dialogActions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
  color: white;
}

button:disabled {
  background: #cccccc;
  cursor: not-allowed;
}

button:hover:not(:disabled) {
  opacity: 0.9;
}
</style>
